// One icon per entry in AppShell's own NAV_SECTIONS, drawn on the same
// 16-unit grid and stroke convention as features/theme/ThemeIcons.jsx — a
// shape for what the destination actually IS, not a decoration, so the rail
// can be scanned by silhouette before a single label is read.
//
// Stroke and fill come from `currentColor` via .nav-icon in shell.css, so
// each icon inherits its link's own colour — quiet grey at rest, the accent
// the moment it is the active page — and no colour is declared here.

function Icon({ children }) {
  return (
    <svg
      className="nav-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Overview — four tiles, because that is what a dashboard literally is: a
 *  grid of separate readings gathered onto one screen. */
export function DashboardIcon() {
  return (
    <Icon>
      <rect x="1.6" y="1.6" width="5.4" height="5.4" rx="1" />
      <rect x="9" y="1.6" width="5.4" height="5.4" rx="1" />
      <rect x="1.6" y="9" width="5.4" height="5.4" rx="1" />
      <rect x="9" y="9" width="5.4" height="5.4" rx="1" />
    </Icon>
  )
}

/** Insights — a line that rises across a frame: how things are moving, as
 *  against Reports' bars, which are the numbers themselves. */
export function InsightsIcon() {
  return (
    <Icon>
      <path d="M1.6 1.6v12.8h12.8" />
      <path d="M4 11l3-3.4 2.4 2L13.6 4.6" />
    </Icon>
  )
}

/** Customers — who the shop bills. */
export function CustomersIcon() {
  return (
    <Icon>
      <circle cx="8" cy="5.4" r="2.6" />
      <path d="M2.6 14.2c0-3 2.4-5.3 5.4-5.3s5.4 2.3 5.4 5.3" />
    </Icon>
  )
}

/** Suppliers — who the shop buys from, drawn as the delivery rather than the
 *  business behind it: this is the one thing every supplier has in common. */
export function SuppliersIcon() {
  return (
    <Icon>
      <path d="M1.4 5.2h8v6H1.4z" />
      <path d="M9.4 7.4h2.9l1.9 2.2v1.6h-1.6" />
      <circle cx="4.2" cy="12.6" r="1.3" />
      <circle cx="11.2" cy="12.6" r="1.3" />
    </Icon>
  )
}

/** Stock Details — the product master, drawn as the one thing every row on
 *  it actually is: a box. */
export function StockIcon() {
  return (
    <Icon>
      <path d="M8 1.6 14 4.8v6.4L8 14.4 2 11.2V4.8Z" />
      <path d="M2 4.8 8 8l6-3.2M8 8v6.4" />
    </Icon>
  )
}

/** Product requests — a want, not a document: a list with the one line
 *  still missing from it. */
export function ProductRequestIcon() {
  return (
    <Icon>
      <rect x="3.2" y="2.4" width="9.6" height="11.6" rx="1.2" />
      <path d="M6 2.4h4v1.8H6z" />
      <path d="M8 7.4v4M6 9.4h4" />
    </Icon>
  )
}

/** Billing — the counter, where a sale is rung up. */
export function BillingIcon() {
  return (
    <Icon>
      <path d="M1.6 2.4h1.8l1.6 8h7.2l1.4-5.6H4.6" />
      <circle cx="6.2" cy="13" r="1" />
      <circle cx="11.2" cy="13" r="1" />
    </Icon>
  )
}

/** Invoices — the printed document itself, not the counter that produced it. */
export function InvoicesIcon() {
  return (
    <Icon>
      <path d="M4.2 1.6h5l2.4 2.4v10.4h-7.4Z" />
      <path d="M9.2 1.6v2.4h2.4" />
      <path d="M6 8h4.4M6 10.4h4.4" />
    </Icon>
  )
}

/** Receipts — a till slip, torn off along the bottom: the paper a customer
 *  walks away with once they have paid. */
export function ReceiptsIcon() {
  return (
    <Icon>
      <path d="M3.6 1.6h8.8v12.8l-1.47-1.2-1.46 1.2-1.47-1.2-1.47 1.2-1.46-1.2-1.47 1.2Z" />
      <path d="M6 5.2h4M6 7.6h4M6 10h2.4" />
    </Icon>
  )
}

/** Purchases — stock settling into the tray rather than out of it, the one
 *  directional fact that tells this apart from every other document icon
 *  on the rail. */
export function PurchasesIcon() {
  return (
    <Icon>
      <path d="M8 2v7.4M5.2 7l2.8 2.8L10.8 7" />
      <path d="M2.4 13h11.2" />
    </Icon>
  )
}

/** Expenses — a wallet with money going out of it: what the shop spends on
 *  itself, as against Purchases' stock coming in. */
export function ExpensesIcon() {
  return (
    <Icon>
      <path d="M2 4.4h10.4a1.6 1.6 0 0 1 1.6 1.6v6.4a1.6 1.6 0 0 1-1.6 1.6H3.6A1.6 1.6 0 0 1 2 12.4Z" />
      <path d="M2 4.4l8.4-2.6v2.6" />
      <path d="M10.8 9.2h3.2" />
    </Icon>
  )
}

/** Money changing hands, either direction — one coin shared by Customer
 *  Payments and Supplier Payments, the same way the label is what tells the
 *  two apart rather than the glyph. */
export function PaymentIcon() {
  return (
    <Icon>
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="8" cy="8" r="3" />
    </Icon>
  )
}

/** Outstanding — what is still owed, and how long it has been owed. */
export function OutstandingIcon() {
  return (
    <Icon>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.4V8l2.8 1.8" />
    </Icon>
  )
}

/** Reports — the numbers, read as bars rather than rows. */
export function ReportsIcon() {
  return (
    <Icon>
      <path d="M1.8 14.2V9.4M7 14.2V5M12.2 14.2V2.6" />
      <path d="M1.4 14.2h13.2" />
    </Icon>
  )
}

/** Settings — sliders, not a gear: three dials read cleanly at 16px, a
 *  gear's teeth blur into a circle at this size. */
export function SettingsIcon() {
  return (
    <Icon>
      <path d="M1.6 4h6.6M11 4h3.4M1.6 8h3.2M7.6 8h6.8M1.6 12h8.2M12.6 12h1.8" />
      <circle cx="9.4" cy="4" r="1.4" />
      <circle cx="5.4" cy="8" r="1.4" />
      <circle cx="11.4" cy="12" r="1.4" />
    </Icon>
  )
}

/** Tenants — platform support's one item: a shop, viewed from outside it. */
export function TenantsIcon() {
  return (
    <Icon>
      <path d="M2.8 14.2V3.4h6.4v10.8" />
      <path d="M9.2 7h3.6v7.2H9.2" />
      <path d="M4.8 5.8h1.2M4.8 8.4h1.2M4.8 11h1.2" />
    </Icon>
  )
}

/** Bulk import — a sheet with an arrow coming in: a file of records arriving
 *  into the shop rather than one record being typed. */
export function ImportIcon() {
  return (
    <Icon>
      <path d="M4 1.6h5.4l2.6 2.6v3" />
      <path d="M4 1.6v12.8h4" />
      <path d="M9.4 1.6v2.6h2.6" />
      <path d="M14.4 11.4h-4.8M11.6 9.4l-2 2 2 2" />
    </Icon>
  )
}
